import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

/**
 * Select de una sola opción con buscador incorporado, al estilo de los
 * dropdowns del resto de la app (input + lista con radios). A diferencia del
 * `DropDownListCustomizedFilter` global, el panel se dibuja en un portal con
 * posición fija, por lo que nunca queda recortado por contenedores con
 * `overflow` (p. ej. la tabla de asignación o el cuerpo del modal).
 */
export interface SearchSelectOption {
  code: string;
  name: string;
}

interface SearchSelectProps {
  value: string;
  options: SearchSelectOption[];
  onChange: (code: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  ariaLabel?: string;
}

interface PanelCoords {
  left: number;
  top: number;
  width: number;
  placement: "down" | "up";
}

const PANEL_MAX_HEIGHT = 260;

const SearchSelect = ({
  value,
  options,
  onChange,
  placeholder = "Seleccionar",
  searchPlaceholder = "Buscar",
  disabled = false,
  clearable = true,
  ariaLabel,
}: SearchSelectProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [coords, setCoords] = useState<PanelCoords>();

  const selected = useMemo(
    () => options.find((option) => option.code === value),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((option) => option.name.toLowerCase().includes(q));
  }, [options, query]);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const placement: "down" | "up" =
      spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow ? "up" : "down";
    setCoords({
      left: rect.left,
      width: rect.width,
      top: placement === "down" ? rect.bottom + 4 : rect.top - 4,
      placement,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    // El scroll dentro de la propia lista NO debe cerrar el panel; cualquier
    // otro scroll (cuerpo del modal, tabla, ventana) solo lo reposiciona para
    // que siga pegado al control.
    const onScroll = (event: Event) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      place();
    };
    const onResize = () => place();
    // En captura y marcando el evento: el Esc que cierra el desplegable no debe
    // cerrar además el modal que lo contiene (ver useEscapeKey).
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, place]);

  const toggle = () => {
    if (disabled) return;
    setQuery("");
    setOpen((current) => !current);
  };

  const pick = (code: string) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <div className="inhab-nomina__ss">
      <button
        type="button"
        ref={triggerRef}
        className={`inhab-nomina__ss-trigger ${open ? "is-open" : ""}`}
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="inhab-nomina__ss-value">
          {selected ? selected.name : <em>{placeholder}</em>}
        </span>
        {clearable && selected && !disabled && (
          <span
            className="inhab-nomina__ss-clear"
            role="button"
            aria-label="Quitar selección"
            onClick={(event) => {
              event.stopPropagation();
              onChange("");
            }}
          >
            <CloseIcon />
          </span>
        )}
        <KeyboardArrowDownIcon className="inhab-nomina__ss-arrow" />
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            className={`inhab-nomina__ss-panel ${
              coords.placement === "up" ? "is-up" : ""
            }`}
            style={{
              position: "fixed",
              left: coords.left,
              width: coords.width,
              top: coords.top,
              transform:
                coords.placement === "up" ? "translateY(-100%)" : undefined,
            }}
          >
            <div className="inhab-nomina__ss-search">
              <SearchIcon />
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
            <div className="inhab-nomina__ss-options" role="listbox">
              {filtered.map((option) => {
                const active = option.code === value;
                return (
                  <button
                    type="button"
                    key={option.code}
                    role="option"
                    aria-selected={active}
                    className={`inhab-nomina__ss-option ${
                      active ? "is-active" : ""
                    }`}
                    onClick={() => pick(option.code)}
                  >
                    {active ? (
                      <RadioButtonCheckedIcon />
                    ) : (
                      <RadioButtonUncheckedIcon />
                    )}
                    <span>{option.name}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="inhab-nomina__ss-empty">
                  {query ? "No hay resultados." : "Sin opciones."}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SearchSelect;
